// =============================================================================
// MeuDriver — Localidades (filtro de instrutores por estado → cidade)
// =============================================================================
// Municípios vêm da API pública do IBGE (lista oficial, por UF); o centro da
// cidade, para "procurar nas proximidades", vem do Nominatim (OpenStreetMap).
// Os dois ficam em cache por muito tempo — mudam quase nunca.

import { useQuery } from '@tanstack/react-query'

export const UFS: { uf: string; nome: string }[] = [
  { uf: 'AC', nome: 'Acre' }, { uf: 'AL', nome: 'Alagoas' }, { uf: 'AP', nome: 'Amapá' },
  { uf: 'AM', nome: 'Amazonas' }, { uf: 'BA', nome: 'Bahia' }, { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' }, { uf: 'GO', nome: 'Goiás' },
  { uf: 'MA', nome: 'Maranhão' }, { uf: 'MT', nome: 'Mato Grosso' }, { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' }, { uf: 'PA', nome: 'Pará' }, { uf: 'PB', nome: 'Paraíba' },
  { uf: 'PR', nome: 'Paraná' }, { uf: 'PE', nome: 'Pernambuco' }, { uf: 'PI', nome: 'Piauí' },
  { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' }, { uf: 'RS', nome: 'Rio Grande do Sul' },
  { uf: 'RO', nome: 'Rondônia' }, { uf: 'RR', nome: 'Roraima' }, { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' }, { uf: 'SE', nome: 'Sergipe' }, { uf: 'TO', nome: 'Tocantins' },
]

/** Raio de "procurar nas proximidades". */
export const NEARBY_KM = 50

/** Municípios da UF, em ordem alfabética (IBGE). */
export function useCidades(uf: string) {
  return useQuery<string[]>({
    queryKey: ['ibge-municipios', uf],
    enabled: Boolean(uf),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24,
    queryFn: async () => {
      const res = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`)
      if (!res.ok) throw new Error('Não foi possível carregar as cidades.')
      const data = (await res.json()) as { nome: string }[]
      return data.map((m) => m.nome)
    },
  })
}

/** Centro aproximado da cidade (lat/lng) para a busca por proximidade. */
export function useCentroDaCidade(cidade: string, uf: string, enabled: boolean) {
  return useQuery<{ lat: number; lng: number } | null>({
    queryKey: ['geo-cidade', uf, cidade],
    enabled: enabled && Boolean(cidade && uf),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24,
    queryFn: async () => {
      const estado = UFS.find((u) => u.uf === uf)?.nome ?? uf
      const params = new URLSearchParams({ city: cidade, state: estado, country: 'Brasil', format: 'json', limit: '1' })
      const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'Accept-Language': 'pt-BR' } })
      if (!res.ok) return null
      const data = (await res.json()) as { lat: string; lon: string }[]
      return data[0] ? { lat: Number(data[0].lat), lng: Number(data[0].lon) } : null
    },
  })
}
